import VoiceInput, { deferDictationBlur, stopDictationFor } from '../components/VoiceInput';
import { linkifyHtml } from '../components/linkifyHtml';
import ReorderTree from '../components/ReorderTree';
import LinkedText from '../components/LinkedText';
import '../components/content.css';
import api from '../api';
import { folderStorageKey, readFolderState, applyFolderState, saveFolderState } from "../components/taskFolderState";
import usePanelWidth from "../components/usePanelWidth";
import PanelResizeHandle from "../components/PanelResizeHandle";
import ImageAnnotationEditor from "../components/ImageAnnotationEditor";
import {
    Box, Sheet, Stack, Typography, IconButton,
    Input, Tooltip, Divider, Button, Chip, CircularProgress,
} from "@mui/joy";
import { useState, useRef, useCallback, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
// ─── Icons ────────────────────────────────────────────────────────────────────
import FolderRoundedIcon                  from "@mui/icons-material/FolderRounded";
import FolderOpenRoundedIcon              from "@mui/icons-material/FolderOpenRounded";
import ChevronRightRoundedIcon            from "@mui/icons-material/ChevronRightRounded";
import DeleteOutlineRoundedIcon           from "@mui/icons-material/DeleteOutlineRounded";
import CreateNewFolderRoundedIcon         from "@mui/icons-material/CreateNewFolderRounded";
import AddTaskRoundedIcon                 from "@mui/icons-material/AddTaskRounded";
import CheckBoxOutlineBlankRoundedIcon    from "@mui/icons-material/CheckBoxOutlineBlankRounded";
import CheckBoxRoundedIcon                from "@mui/icons-material/CheckBoxRounded";
import RadioButtonUncheckedRoundedIcon    from "@mui/icons-material/RadioButtonUncheckedRounded";
import RadioButtonCheckedRoundedIcon      from "@mui/icons-material/RadioButtonCheckedRounded";
import AddRoundedIcon                     from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon                  from "@mui/icons-material/DeleteRounded";
import SendRoundedIcon                    from "@mui/icons-material/SendRounded";
import FormatQuoteRoundedIcon             from "@mui/icons-material/FormatQuoteRounded";
import FormatListBulletedRoundedIcon      from "@mui/icons-material/FormatListBulletedRounded";
import FormatListNumberedRoundedIcon      from "@mui/icons-material/FormatListNumberedRounded";
import AssignmentRoundedIcon              from "@mui/icons-material/AssignmentRounded";
import DriveFileRenameOutlineRoundedIcon  from "@mui/icons-material/DriveFileRenameOutlineRounded";
import FormatColorTextRoundedIcon         from "@mui/icons-material/FormatColorTextRounded";
import FormatBoldRoundedIcon              from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRoundedIcon            from "@mui/icons-material/FormatItalicRounded";
import MoreHorizRoundedIcon               from "@mui/icons-material/MoreHorizRounded";
import LocalFireDepartmentRoundedIcon     from "@mui/icons-material/LocalFireDepartmentRounded";
import SubdirectoryArrowRightRoundedIcon  from "@mui/icons-material/SubdirectoryArrowRightRounded";
import ViewListRoundedIcon                from "@mui/icons-material/ViewListRounded";
import CheckRoundedIcon                   from "@mui/icons-material/CheckRounded";
import RefreshRoundedIcon                 from "@mui/icons-material/RefreshRounded";
import DragIndicatorRoundedIcon           from "@mui/icons-material/DragIndicatorRounded";
import LowPriorityRoundedIcon from "@mui/icons-material/LowPriorityRounded";
import CloseRoundedIcon       from "@mui/icons-material/CloseRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import TableChartRoundedIcon        from "@mui/icons-material/TableChartRounded";
import ImageRoundedIcon             from "@mui/icons-material/ImageRounded";
import AttachFileRoundedIcon        from "@mui/icons-material/AttachFileRounded";
import DownloadRoundedIcon          from "@mui/icons-material/DownloadRounded";
import InsertDriveFileRoundedIcon   from "@mui/icons-material/InsertDriveFileRounded";
import { keyframes } from "@mui/system";
import ImageViewer from "../components/ImageViewer";


import {
    APIGetFolders,
    APIGetTaskList, APICreateFolder, APIUpdateFolder, APIDeleteFolder,
    APICreateTaskList, APIUpdateTaskList, APIDeleteTaskList,
    APICreateSection, APIUpdateSection, APIDeleteSection,
    APICreateItem, APICreateSubtask, APIUpdateItem, APIDeleteItem,
    APICreateComment, APIUpdateComment, APIDeleteComment,
    mediaUrl,
    APIReorderTree, APICreateTaskListRoot
} from "../api";
import { useSearchParams } from "react-router";
import { LayoutContext } from "../context";
import { useWindowWidth } from "../hooks";



// ─── Constants ────────────────────────────────────────────────────────────────

const FLAME_OPTIONS = [
    { value: null,     label: "Без огонька", color: "#ccc" },
    { value: "yellow", label: "Важно",       color: "#f59e0b" },
    { value: "red",    label: "Срочно",      color: "#ef4444" },
];

// ─── «Горящая» плашка важной задачи ───────────────────────────────────────────
// Мягкое пульсирующее сияние + плывущий тёплый градиент. Без резкого мигания.
const flameGlowRed = keyframes`
    0%, 100% { background-position: 0% 50%;   box-shadow: 0 0 0 rgba(239,68,68,0); }
    50%      { background-position: 100% 50%; box-shadow: 0 0 12px rgba(239,68,68,0.28); }
`;
const flameGlowYellow = keyframes`
    0%, 100% { background-position: 0% 50%;   box-shadow: 0 0 0 rgba(245,158,11,0); }
    50%      { background-position: 100% 50%; box-shadow: 0 0 10px rgba(245,158,11,0.24); }
`;

// Возвращает sx для строки задачи в зависимости от flame. Если flame нет — {}.
function flameRowSx(flame) {
    if (flame === "red") return {
        border: "1px solid rgba(239,68,68,0.35)",
        backgroundImage: "linear-gradient(100deg, rgba(239,68,68,0.10), rgba(249,115,22,0.13), rgba(239,68,68,0.10))",
        backgroundSize: "200% 100%",
        animation: `${flameGlowRed} 3.2s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none", backgroundPosition: "50% 50%" },
    };
    if (flame === "yellow") return {
        border: "1px solid rgba(245,158,11,0.32)",
        backgroundImage: "linear-gradient(100deg, rgba(245,158,11,0.10), rgba(250,204,21,0.15), rgba(245,158,11,0.10))",
        backgroundSize: "200% 100%",
        animation: `${flameGlowYellow} 3.6s ease-in-out infinite`,
        "@media (prefers-reduced-motion: reduce)": { animation: "none", backgroundPosition: "50% 50%" },
    };
    return {};
}

const ITEM_TYPES = [
    { value: "checkbox", label: "Чекбокс",    icon: <CheckBoxOutlineBlankRoundedIcon sx={{ fontSize: 15 }} /> },
    { value: "radio",    label: "Радиокнопка", icon: <RadioButtonUncheckedRoundedIcon sx={{ fontSize: 15 }} /> },
    { value: "bullet",   label: "Маркер",      icon: <FormatListBulletedRoundedIcon   sx={{ fontSize: 15 }} /> },
    { value: "numbered", label: "Нумерация",   icon: <FormatListNumberedRoundedIcon   sx={{ fontSize: 15 }} /> },
    { value: "text",     label: "Текст",       icon: <DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 15 }} /> },
];

// ─── IDs / utils ──────────────────────────────────────────────────────────────
let _nextId = 30;
const genId = () => `tmp-${++_nextId}`;
const fmtNow = () => {
    const d = new Date();
    return `${d.getDate()} ${["янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек"][d.getMonth()]}, ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
};
const confirmDelete = (onConfirm) => {
    if (window.confirm("Удалить? Это действие нельзя отменить.")) onConfirm();
};

const COUNTABLE_TYPES = ["checkbox", "radio"];

// Рекурсивно собирает все countable задачи (item + subtasks)
function collectCountable(items) {
    const result = [];
    for (const item of items) {
        if (COUNTABLE_TYPES.includes(item.type)) result.push(item);
        if (item.subtasks?.length) result.push(...collectCountable(item.subtasks));
    }
    return result;
}

// ─── Server → local shape converters ─────────────────────────────────────────
// Backend uses snake_case — we normalise once so all existing rendering stays unchanged.

function normaliseComment(raw) {
    return {
        id:        raw.id,
        text:      raw.text ?? "",
        quoted:    raw.is_quoted ?? false,
        createdAt: raw.created_at ?? fmtNow(),
        updatedAt: raw.updated_at ?? null,
        authorName: raw.author_name ?? null,
        attachments: (raw.attachments ?? []).map(a => ({
            id:   a.id,
            url:  a.url,
            kind: a.kind,                       // "image" | "file"
            name: a.original_name ?? "",
            mime: a.mime_type ?? "",
            size: a.size ?? 0,
        })),
    };
}
function normaliseItem(raw) {
    return {
        id:       raw.id,
        type:     raw.item_type ?? "checkbox",
        text:     raw.text ?? "",
        done:     raw.done ?? false,
        flame:    raw.flame || null,
        expanded: raw.is_expanded ?? false,
        comments: (raw.comments ?? []).map(normaliseComment),
        subtasks: (raw.subtasks  ?? []).map(normaliseItem),
    };
}
function normaliseSection(raw) {
    return {
        id:    raw.id,
        title: raw.title ?? "",
        items: (raw.items ?? []).map(normaliseItem),
    };
}
function normaliseList(raw) {
    return {
        id:       raw.id,
        type:     "tasklist",
        name:     raw.name ?? "Список",
        priority: raw.priority ?? "medium",
        sections: (raw.sections ?? []).map(normaliseSection),
        _loaded: Array.isArray(raw.sections),
    };
}
function normaliseFolder(raw) {
    return {
        id:       raw.id,
        type:     raw.type ?? "folder",
        name:     raw.name ?? "Папка",
        open:     raw.is_open ?? raw.open ?? false,
        children: (raw.children ?? raw.lists ?? []).map(ch =>
            ch.type === "folder" ? normaliseFolder(ch) : normaliseList(ch)
        ),
    };
}

// local patch → API field names
function itemPatchToApi(patch) {
    const m = {};
    if (patch.done      !== undefined) m.done        = patch.done;
    if (patch.text      !== undefined) m.text        = patch.text;
    if (patch.flame     !== undefined) m.flame       = patch.flame ?? "";
    if (patch.type      !== undefined) m.item_type   = patch.type;
    if (patch.expanded  !== undefined) m.is_expanded = patch.expanded;
    return m;
}

// ─── Tree helpers ─────────────────────────────────────────────────────────────
function findNode(nodes, id) {
    for (const n of nodes) {
        if (n.id === id) return n;
        if (n.children) { const f = findNode(n.children, id); if (f) return f; }
    }
    return null;
}
function updateNode(nodes, id, updater) {
    return nodes.map(n => {
        if (n.id === id) return updater(n);
        if (n.children) return { ...n, children: updateNode(n.children, id, updater) };
        return n;
    });
}
function deleteNode(nodes, id) {
    return nodes.filter(n => n.id !== id)
        .map(n => n.children ? { ...n, children: deleteNode(n.children, id) } : n);
}
function addToNode(nodes, parentId, newNode) {
    if (parentId === "root") return [...nodes, newNode];
    return nodes.map(n => {
        if (n.id === parentId && n.type === "folder")
            return { ...n, open: true, children: [...(n.children || []), newNode] };
        if (n.children) return { ...n, children: addToNode(n.children, parentId, newNode) };
        return n;
    });
}

// Recursive item helpers
function updateItemRec(items, id, fn) {
    return items.map(it => {
        if (it.id === id) return fn(it);
        if (it.subtasks?.length) return { ...it, subtasks: updateItemRec(it.subtasks, id, fn) };
        return it;
    });
}
function deleteItemRec(items, id) {
    return items
        .filter(it => it.id !== id)
        .map(it => it.subtasks?.length ? { ...it, subtasks: deleteItemRec(it.subtasks, id) } : it);
}
function addSubtaskRec(items, parentId, newItem) {
    return items.map(it => {
        if (it.id === parentId) return { ...it, subtasks: [...(it.subtasks || []), newItem], expanded: true };
        if (it.subtasks?.length) return { ...it, subtasks: addSubtaskRec(it.subtasks, parentId, newItem) };
        return it;
    });
}
// ─── ItemIcon ─────────────────────────────────────────────────────────────────
function ItemIcon({ type, done, index, onToggle }) {
    const base = { fontSize: 20, flexShrink: 0, cursor: "pointer" };
    if (type === "checkbox") return done
        ? <CheckBoxRoundedIcon sx={{ ...base, color: "primary.400" }} onClick={onToggle} />
        : <CheckBoxOutlineBlankRoundedIcon sx={{ ...base, color: "text.tertiary" }} onClick={onToggle} />;
    if (type === "radio") return done
        ? <RadioButtonCheckedRoundedIcon sx={{ ...base, color: "primary.400" }} onClick={onToggle} />
        : <RadioButtonUncheckedRoundedIcon sx={{ ...base, color: "text.tertiary" }} onClick={onToggle} />;
    if (type === "bullet") return (
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "primary.400", flexShrink: 0 }} />
    );
    if (type === "numbered") return (
        <Typography sx={{ fontSize: "0.8rem", color: "primary.400", fontWeight: 700, minWidth: 18, flexShrink: 0, lineHeight: 1 }}>
            {(index ?? 0) + 1}.
        </Typography>
    );
    return <ViewListRoundedIcon sx={{ fontSize: 14, color: "text.tertiary", flexShrink: 0 }} />;
}

// ─── FlameButton ──────────────────────────────────────────────────────────────
function FlameButton({ flame, onChange }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos]   = useState({ top: 0, left: 0 });
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);
    const flameColor = flame === "red" ? "#ef4444" : flame === "yellow" ? "#f59e0b" : null;
    return (
        <Box ref={ref} sx={{ position: "relative" }}>
            <Tooltip title="Огонёк" size="sm">
                <IconButton size="sm" variant="plain"
                    sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7, color: flameColor || "text.tertiary" }}
                    onMouseDown={e => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setPos({ top: rect.bottom + 4, left: rect.right - 140 });
                        setOpen(v => !v);
                    }}
                >
                    <LocalFireDepartmentRoundedIcon sx={{ fontSize: 15, color: flameColor || "inherit" }} />
                </IconButton>
            </Tooltip>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.95 }} transition={{ duration: 0.12 }}
                        style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999,
                            background: "var(--ld-surface)", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.14)", padding: "6px", minWidth: 140 }}
                    >
                        {FLAME_OPTIONS.map(opt => (
                            <Box key={String(opt.value)}
                                onMouseDown={e => { e.stopPropagation(); onChange(opt.value); setOpen(false); }}
                                sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.2, py: 0.65, borderRadius: 8, cursor: "pointer",
                                    bgcolor: flame === opt.value ? "primary.softBg" : "transparent", "&:hover": { bgcolor: "neutral.softBg" } }}
                            >
                                <LocalFireDepartmentRoundedIcon sx={{ fontSize: 15, color: opt.color }} />
                                <Typography sx={{ fontSize: "0.82rem" }}>{opt.label}</Typography>
                            </Box>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}

// ─── TypeMenu ─────────────────────────────────────────────────────────────────
function TypeMenu({ type, onChange }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos]   = useState({ top: 0, left: 0 });
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);
    return (
        <Box ref={ref} sx={{ position: "relative" }}>
            <Tooltip title="Тип элемента" size="sm">
                <IconButton size="sm" variant="plain" color="neutral"
                    sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                    onMouseDown={e => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setPos({ top: rect.bottom + 4, left: rect.right - 140 });
                        setOpen(v => !v);
                    }}
                >
                    <MoreHorizRoundedIcon sx={{ fontSize: 15 }} />
                </IconButton>
            </Tooltip>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -6, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -6, scale: 0.95 }} transition={{ duration: 0.12 }}
                        style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999,
                            background: "var(--ld-surface)", borderRadius: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.14)", padding: "6px", minWidth: 140 }}
                    >
                        {ITEM_TYPES.map(t => (
                            <Box key={t.value}
                                onMouseDown={e => { e.stopPropagation(); onChange(t.value); setOpen(false); }}
                                sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.2, py: 0.65, borderRadius: 8, cursor: "pointer",
                                    bgcolor: type === t.value ? "primary.softBg" : "transparent", "&:hover": { bgcolor: "neutral.softBg" } }}
                            >
                                <Box sx={{ color: type === t.value ? "primary.400" : "text.tertiary" }}>{t.icon}</Box>
                                <Typography sx={{ fontSize: "0.83rem" }}>{t.label}</Typography>
                            </Box>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}

// ─── CommentEditor ────────────────────────────────────────────────────────────
// Универсальный: создание нового и правка существующего комментария.
const MAX_ATTACH_SIZE = 25 * 1024 * 1024; // 25 МБ — синхронно с бэкендом

function fmtSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function CommentEditor({
    onSubmit, onCancel,
    initialHtml = "", initialQuoted = false, initialAttachments = [],
    submitLabel = "Отправить", sending = false,
}) {
    const editorRef = useRef(null);
    const fileInputRef = useRef(null);
    const imageInputRef = useRef(null);

    const [annotation, setAnnotation] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const [quoted, setQuoted] = useState(initialQuoted);
    const [pending, setPending] = useState([]);          // новые файлы { key, file, isImage, preview }
    const [kept, setKept]       = useState(initialAttachments); // существующие вложения (режим правки)
    const [removed, setRemoved] = useState([]);          // id откреплённых существующих
    const [error, setError]     = useState("");

    // держим актуальный pending в ref, чтобы почистить objectURL на размонтировании
    const pendingRef = useRef([]);
    pendingRef.current = pending;

    useEffect(() => {
        if (editorRef.current && initialHtml) editorRef.current.innerHTML = initialHtml;
        const raf = requestAnimationFrame(() => editorRef.current?.focus());
        return () => cancelAnimationFrame(raf);
    }, []);

    useEffect(() => () => {
        pendingRef.current.forEach(p => { if (p.preview) URL.revokeObjectURL(p.preview); if (p.originalPreview && p.originalPreview !== p.preview) URL.revokeObjectURL(p.originalPreview); });
    }, []);

    const addFiles = (fileList) => {
        const next = [];
        const warnings = [];
        for (const f of Array.from(fileList || [])) {
            if (kept.length + pending.length + next.length >= 10) { warnings.push('В одном комментарии можно прикрепить до 10 файлов.'); break; }
            if (f.size > MAX_ATTACH_SIZE) { warnings.push(`«${f.name}» больше 25 МБ — не прикрепил`); continue; }
            const isImage = (f.type || "").startsWith("image/");
            next.push({
                key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                file: f, isImage,
                preview: isImage ? URL.createObjectURL(f) : null,
            });
        }
        if (next.length) setPending(p => [...p, ...next]);
        setError(warnings.join(' '));
    };

    const removePending = (key) => setPending(p => {
        const f = p.find(x => x.key === key);
        if (f?.preview) URL.revokeObjectURL(f.preview);
        if (f?.originalPreview && f.originalPreview !== f.preview) URL.revokeObjectURL(f.originalPreview);
        return p.filter(x => x.key !== key);
    });

    const detachExisting = (id) => {
        setKept(k => k.filter(a => a.id !== id));
        setRemoved(r => [...r, id]);
    };

    const annotatePending = (p) => setAnnotation({ key: p.key, src: p.originalPreview || p.preview, name: p.file.name, strokes: p.strokes || [] });
    const annotateExisting = (a) => setAnnotation({ key: `existing-${a.id}`, existingId: a.id, src: mediaUrl(a.url), name: a.name });
    const applyAnnotation = (file, strokes) => {
        const preview = URL.createObjectURL(file);
        if (annotation.existingId) {
            detachExisting(annotation.existingId);
            setPending(prev => [...prev, { key: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file, isImage: true, preview }]);
        } else {
            setPending(prev => prev.map(p => {
                if (p.key !== annotation.key) return p;
                const originalPreview = p.originalPreview || p.preview;
                if (p.preview !== originalPreview) URL.revokeObjectURL(p.preview);
                return { ...p, file, preview, originalPreview, strokes };
            }));
        }
        setAnnotation(null);
    };

    const insertTable = () => {
        editorRef.current?.focus();
        const rows = 2, cols = 2;
        let html = '<table><tbody>';
        for (let r = 0; r < rows; r++) {
            html += "<tr>";
            for (let c = 0; c < cols; c++) html += "<td><br></td>";
            html += "</tr>";
        }
        html += "</tbody></table><p><br></p>";
        document.execCommand("insertHTML", false, html);
    };

    const onPaste = (e) => {
        const items = e.clipboardData?.items || [];
        const imgs = [];
        for (const it of items) {
            if (it.kind === "file" && (it.type || "").startsWith("image/")) {
                const f = it.getAsFile();
                if (f) imgs.push(f);
            }
        }
        if (imgs.length) { e.preventDefault(); addFiles(imgs); } // картинку — во вложения, не в HTML
    };

    const submit = async () => {
        if (sending || submittingRef.current || annotation) return;
        const html = editorRef.current?.innerHTML || '';
        const isEmpty = !html || html === '<br>' || html.replace(/<br\s*\/?>/gi, '').trim() === '';
        if (isEmpty && !pending.length && !kept.length) return;
        submittingRef.current = true; setSubmitting(true); setError('');
        try {
            await onSubmit({ text: html, quoted, files: pending.map(p => p.file), removeAttachmentIds: removed });
            if (editorRef.current) editorRef.current.innerHTML = '';
            pending.forEach(p => { if (p.preview) URL.revokeObjectURL(p.preview); if (p.originalPreview && p.originalPreview !== p.preview) URL.revokeObjectURL(p.originalPreview); });
            setPending([]); setRemoved([]); setQuoted(false);
        } catch {
            setError('Не удалось сохранить комментарий. Текст и вложения остались в редакторе — попробуйте ещё раз.');
        } finally { submittingRef.current = false; setSubmitting(false); }
    };

    const tbBtn = { width: 26, height: 26, minWidth: 0, borderRadius: 7 };

    return (
        <Box sx={{ border: "1.5px solid", borderColor: "divider", borderRadius: 14, overflow: "hidden",
            "&:focus-within": { borderColor: "primary.300" }, transition: "border-color 0.15s" }}>
            {/* Тулбар */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.5, flexWrap: "wrap",
                bgcolor: "neutral.50", borderBottom: "1px solid", borderColor: "divider" }}>
                <Tooltip title="Жирный" size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" sx={tbBtn}
                        onMouseDown={e => { e.preventDefault(); document.execCommand("bold"); }}
                    ><FormatBoldRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
                <Tooltip title="Курсив" size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" sx={tbBtn}
                        onMouseDown={e => { e.preventDefault(); document.execCommand("italic"); }}
                    ><FormatItalicRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
                <Tooltip title="Выделить" size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" sx={tbBtn}
                        onMouseDown={e => { e.preventDefault(); document.execCommand("hiliteColor", false, "#fef08a"); }}
                    ><FormatColorTextRoundedIcon sx={{ fontSize: 14, color: "#ca8a04" }} /></IconButton>
                </Tooltip>
                <Divider orientation="vertical" sx={{ mx: 0.5, height: 16 }} />
                <Tooltip title="Таблица 2×2" size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" sx={tbBtn}
                        onMouseDown={e => { e.preventDefault(); insertTable(); }}
                    ><TableChartRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
                <Tooltip title="Картинка" size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" sx={tbBtn}
                        onClick={() => imageInputRef.current?.click()}
                    ><ImageRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
                <Tooltip title="Файл" size="sm">
                    <IconButton size="sm" variant="plain" color="neutral" sx={tbBtn}
                        onClick={() => fileInputRef.current?.click()}
                    ><AttachFileRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
                <Divider orientation="vertical" sx={{ mx: 0.5, height: 16 }} />
                <Tooltip title={quoted ? "Убрать цитату" : "Цитата"} size="sm">
                    <IconButton size="sm" variant={quoted ? "soft" : "plain"} color={quoted ? "primary" : "neutral"}
                        sx={tbBtn}
                        onMouseDown={e => { e.preventDefault(); setQuoted(v => !v); }}
                    ><FormatQuoteRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>

                {/* скрытые input'ы */}
                <input ref={imageInputRef} type="file" accept="image/*" multiple hidden
                    onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
                <input ref={fileInputRef} type="file" multiple hidden
                    onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
            </Box>

            {/* Поле ввода */}
            <Box ref={editorRef} contentEditable suppressContentEditableWarning
                onPaste={onPaste}
                onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submit(); }}
                sx={{ px: 1.5, py: 1, minHeight: 50, outline: "none", fontSize: "0.87rem", lineHeight: 1.6,
                    color: "text.primary",
                    "& mark": { bgcolor: "var(--joy-palette-warning-softBg)", borderRadius: "3px", px: "2px" },
                    "& table": { borderCollapse: "collapse", width: "100%", my: 0.5, fontSize: "0.82rem" },
                    "& td, & th": { border: "1px solid", borderColor: "neutral.300", p: "4px 8px", minWidth: 40 },
                    "&:empty:before": { content: '"Комментарий... (Ctrl+Enter — отправить)"', color: "#bbb", pointerEvents: "none" } }}
            />

            {/* Полоса вложений */}
            {(pending.length > 0 || kept.length > 0) && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, px: 1.5, py: 1,
                    borderTop: "1px solid", borderColor: "divider", bgcolor: "neutral.50" }}>
                    {kept.map(a => (
                        <AttachChip key={a.id} kind={a.kind} src={a.kind === "image" ? mediaUrl(a.url) : null}
                            name={a.name} size={a.size} onRemove={() => detachExisting(a.id)} onAnnotate={a.kind === "image" ? () => annotateExisting(a) : undefined} />
                    ))}
                    {pending.map(p => (
                        <AttachChip key={p.key} kind={p.isImage ? "image" : "file"} src={p.preview}
                            name={p.file.name} size={p.file.size} onRemove={() => removePending(p.key)} onAnnotate={p.isImage ? () => annotatePending(p) : undefined} />
                    ))}
                </Box>
            )}

            {error && (
                <Typography sx={{ px: 1.5, py: 0.5, fontSize: "0.75rem", color: "danger.500", bgcolor: "danger.50" }}>
                    {error}
                </Typography>
            )}

            <AnimatePresence>{annotation && <ImageAnnotationEditor key={annotation.key} image={annotation} onClose={() => setAnnotation(null)} onApply={applyAnnotation} />}</AnimatePresence>

            {/* Кнопки */}
            <Typography level="body-xs" sx={{ px: 1.5, py: .5, color: 'text.tertiary' }}>Вложения: {kept.length + pending.length}/10 · можно выбрать несколько файлов · до 25 МБ каждый</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 1.5, py: 0.5,
                bgcolor: "neutral.50", borderTop: "1px solid", borderColor: "divider" }}>
                {onCancel && (
                    <Button size="sm" variant="plain" color="neutral" onClick={onCancel} disabled={sending || submitting}
                        sx={{ borderRadius: 9, height: 26, px: 1.2, fontSize: "0.79rem" }}>Отмена</Button>
                )}
                <Button size="sm" variant="solid" color="primary" disabled={sending || submitting}
                    startDecorator={sending || submitting
                        ? <CircularProgress size="sm" sx={{ "--CircularProgress-size": "12px" }} />
                        : <SendRoundedIcon sx={{ fontSize: 12 }} />}
                    onClick={submit} sx={{ borderRadius: 9, height: 26, px: 1.2, fontSize: "0.79rem" }}
                >{submitLabel}</Button>
            </Box>
        </Box>
    );
}

// Мини-плашка вложения в редакторе (превью картинки или иконка файла) с крестиком.
function AttachChip({ kind, src, name, size, onRemove, onAnnotate }) {
    return (
        <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 0.75,
            border: "1px solid", borderColor: "neutral.200", borderRadius: 10, p: 0.5,
            bgcolor: "background.surface", maxWidth: 280, minWidth: 0 }}>
            {kind === "image" && src ? (
                <Box component="img" src={src} alt={name}
                    sx={{ width: 34, height: 34, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />
            ) : (
                <Box sx={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, display: "flex",
                    alignItems: "center", justifyContent: "center", bgcolor: "neutral.100" }}>
                    <InsertDriveFileRoundedIcon sx={{ fontSize: 18, color: "text.tertiary" }} />
                </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.74rem", fontWeight: 500, whiteSpace: "nowrap",
                    overflow: "hidden", textOverflow: "ellipsis" }}>{name}</Typography>
                {size ? <Typography sx={{ fontSize: "0.66rem", color: "text.tertiary" }}>{fmtSize(size)}</Typography> : null}
            </Box>
            {onAnnotate && <Tooltip title="Разметить изображение"><IconButton size="sm" variant="soft" aria-label={`Разметить ${name}`} onClick={onAnnotate} sx={{ flexShrink: 0, width: 28, height: 28, minWidth: 28, minHeight: 28, borderRadius: 6 }}><DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>}
            <IconButton size="sm" variant="soft" color="danger" aria-label={`Убрать ${name}`}
                sx={{ flexShrink: 0, width: 24, height: 28, minWidth: 24, minHeight: 28, borderRadius: 6 }}
                onClick={onRemove}
            ><CloseRoundedIcon sx={{ fontSize: 11 }} /></IconButton>
        </Box>
    );
}

// ─── CommentBubble ────────────────────────────────────────────────────────────
function CommentBubble({ c, onDelete, onSave }) {
    const [editing, setEditing] = useState(false);
    const [viewerIndex, setViewerIndex] = useState(null); // индекс открытого фото или null

    const images = (c.attachments || []).filter(a => a.kind === "image");
    const files  = (c.attachments || []).filter(a => a.kind !== "image");

    if (editing) {
        return (
            <Box sx={{ my: 0.5 }}>
                <CommentEditor
                    initialHtml={c.text}
                    initialQuoted={c.quoted}
                    initialAttachments={c.attachments || []}
                    submitLabel="Сохранить"
                    onSubmit={async (payload) => { await onSave(c.id, payload); setEditing(false); }}
                    onCancel={() => setEditing(false)}
                />
            </Box>
        );
    }

    const bodySx = {
        fontSize: "0.83rem", color: "text.secondary", lineHeight: 1.55,
        "& mark": { bgcolor: "var(--joy-palette-warning-softBg)", borderRadius: "3px", px: "2px" },
        "& table": { borderCollapse: "collapse", width: "100%", my: 0.5, fontSize: "0.8rem" },
        "& td, & th": { border: "1px solid", borderColor: "neutral.300", p: "4px 8px" },
        "& img": { maxWidth: "100%", borderRadius: 8 },
    };

    return (
        <Box sx={{ position: "relative" }} className="comment-bubble">
            <Box sx={{
                px: 1.5, py: 0.7, my: 0.5, pr: 6,
                bgcolor: "primary.50", borderRadius: c.quoted ? "0 12px 12px 0" : 12,
                borderLeft: "3px solid", borderColor: c.quoted ? "primary.300" : "primary.400",
            }}>
                <Box component="div" sx={{ ...bodySx, fontStyle: c.quoted ? "italic" : "normal" }}
                    className="ld-rich-text" dangerouslySetInnerHTML={{ __html: linkifyHtml(c.text) }} />

                {/* Превью картинок */}
                {images.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                        {images.map((a, idx) => (
                            <Box key={a.id}
                                onClick={() => setViewerIndex(idx)}
                                sx={{ display: "block", lineHeight: 0, cursor: "zoom-in" }}>
                                <Box component="img" src={mediaUrl(a.url)} alt={a.name}
                                    sx={{ width: 92, height: 92, objectFit: "cover", borderRadius: 10,
                                        border: "1px solid", borderColor: "neutral.200",
                                        transition: "transform 0.15s", "&:hover": { transform: "scale(1.03)" } }} />
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Файлы */}
                {files.length > 0 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.75 }}>
                        {files.map(a => (
                            <Box key={a.id} component="a" href={mediaUrl(a.url)} target="_blank" rel="noreferrer" download
                                sx={{ display: "flex", alignItems: "center", gap: 1, textDecoration: "none",
                                    px: 1, py: 0.5, borderRadius: 9, bgcolor: "background.surface",
                                    border: "1px solid", borderColor: "neutral.200", maxWidth: 320,
                                    "&:hover": { borderColor: "primary.300" } }}>
                                <InsertDriveFileRoundedIcon sx={{ fontSize: 18, color: "text.tertiary", flexShrink: 0 }} />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontSize: "0.78rem", color: "text.primary", whiteSpace: "nowrap",
                                        overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</Typography>
                                    {a.size ? <Typography sx={{ fontSize: "0.66rem", color: "text.tertiary" }}>{fmtSize(a.size)}</Typography> : null}
                                </Box>
                                <DownloadRoundedIcon sx={{ fontSize: 16, color: "text.tertiary", flexShrink: 0 }} />
                            </Box>
                        ))}
                    </Box>
                )}

                <Typography sx={{ fontSize: "0.69rem", color: "text.tertiary", mt: 0.4 }}>
                    {c.authorName ? `${c.authorName} · ` : ""}{c.createdAt}
                    {c.updatedAt ? " · изменено" : ""}
                </Typography>
            </Box>

            {/* Кнопки: правка + удаление */}
            <Box sx={{ position: "absolute", top: 4, right: 4, display: "flex", gap: 0.25,
                opacity: { xs: 0.5, md: 0 }, transition: "opacity 0.15s",
                ".comment-bubble:hover &": { opacity: 0.7 }, "&:hover": { opacity: "1 !important" } }}>
                {onSave && (
                    <Tooltip title="Изменить" size="sm">
                        <IconButton size="sm" variant="plain" color="neutral"
                            sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 8 }}
                            onClick={() => setEditing(true)}
                        ><DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                    </Tooltip>
                )}
                <Tooltip title="Удалить" size="sm">
                    <IconButton size="sm" variant="plain" color="danger"
                        sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 8 }}
                        onClick={() => confirmDelete(() => onDelete(c.id))}
                    ><DeleteRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                </Tooltip>
            </Box>

            {viewerIndex !== null && (
                <ImageViewer
                    images={images.map(a => ({ url: mediaUrl(a.url), name: a.name }))}
                    index={viewerIndex}
                    onClose={() => setViewerIndex(null)}
                />
            )}
        </Box>
    );
}

// ─── ItemMobileMenu ───────────────────────────────────────────────────────────
function ItemMobileMenu({ item, onPatch, onDelete, onAddSubtask, onComment, depth }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos]   = useState({ top: 0, left: 0 });
    const [submenuOpen, setSubmenuOpen] = useState(false);
    const [submenuPos, setSubmenuPos]   = useState({ top: 0, left: 0 });

    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);

    const flameColor = item.flame === "red" ? "#ef4444" : item.flame === "yellow" ? "#f59e0b" : null;

    const actions = [
        {
            label: "Комментарий",
            icon: <FormatQuoteRoundedIcon sx={{ fontSize: 15 }} />,
            onClick: () => onComment(),
        },
        ...(depth === 0 ? [{
            label: "Подзадача",
            icon: <SubdirectoryArrowRightRoundedIcon sx={{ fontSize: 15 }} />,
            onClick: () => onAddSubtask(item.id),
        }] : []),
        {
            label: "Редактировать",
            icon: <DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 15 }} />,
            onClick: () => onPatch(item.id, { _startEdit: true }),
        },
        {
            label: "Тип задачи",
            icon: <FormatListBulletedRoundedIcon sx={{ fontSize: 15 }} />,
            submenu: true,
        },
        {
            label: item.flame ? "Убрать огонёк" : "Срочно 🔴",
            icon: <LocalFireDepartmentRoundedIcon sx={{ fontSize: 15, color: flameColor || "text.tertiary" }} />,
            onClick: () => onPatch(item.id, { flame: item.flame === "red" ? null : "red" }),
        },
        {
            label: item.flame === "yellow" ? "Убрать важность" : "Важно 🟡",
            icon: <LocalFireDepartmentRoundedIcon sx={{ fontSize: 15, color: "#f59e0b" }} />,
            onClick: () => onPatch(item.id, { flame: item.flame === "yellow" ? null : "yellow" }),
        },
        {
            label: "Удалить",
            icon: <DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} />,
            onClick: () => confirmDelete(() => onDelete(item.id)),
            danger: true,
        },
    ];

    return (
        <Box ref={ref} sx={{ position: "relative", flexShrink: 0, display: { xs: "flex", md: "none" } }}>
            <IconButton
                size="sm" variant="plain" color="neutral" aria-label="Действия задачи"
                sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7, opacity: 0.45 }}
                onMouseDown={e => {
                    e.stopPropagation();
                    const rect    = e.currentTarget.getBoundingClientRect();
                    const menuH   = 280; // примерная высота меню
                    const menuW   = 190;
                    const screenH = window.innerHeight;
                    const screenW = window.innerWidth;

                    // Открывать вверх если снизу не хватает места
                    const top  = rect.bottom + menuH > screenH
                        ? rect.top - menuH - 4
                        : rect.bottom + 4;

                    // Не выходить за правый край
                    const left = rect.left + menuW > screenW
                        ? screenW - menuW - 8
                        : rect.left;

                    setPos({ top: Math.max(8, top), left: Math.max(8, left) });
                    setSubmenuOpen(false);
                    setOpen(v => !v);
                }}
            >
                <MoreHorizRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, y: -4 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: -4 }}
                        transition={{ duration: 0.12 }}
                        style={{
                            position: "fixed",
                            top: pos.top, left: pos.left,
                            zIndex: 9999,
                            background: "var(--joy-palette-background-surface)",
                            borderRadius: 12,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.13)",
                            border: "1px solid var(--joy-palette-neutral-200)",
                            padding: "4px",
                            minWidth: 190,
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {actions.map((a, i) => (
                            <Box key={i}
                                onClick={e => {
                                    if (a.submenu) {
                                        const rect    = e.currentTarget.getBoundingClientRect();
                                        const submenuH = 200;
                                        const submenuW = 170;
                                        const screenH  = window.innerHeight;
                                        const screenW  = window.innerWidth;

                                        // Сабменю справа или слева
                                        const left = rect.right + submenuW > screenW
                                            ? rect.left - submenuW - 4
                                            : rect.right + 4;

                                        // Не выходить снизу
                                        const top = rect.top + submenuH > screenH
                                            ? screenH - submenuH - 8
                                            : rect.top;

                                        setSubmenuPos({ top: Math.max(8, top), left: Math.max(8, left) });
                                        setSubmenuOpen(v => !v);
                                        return;
                                    }
                                    a.onClick();
                                    setOpen(false);
                                    setSubmenuOpen(false);
                                }}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1.5,
                                    px: 1.5, py: 0.9, borderRadius: 8, cursor: "pointer",
                                    color: a.danger ? "danger.plainColor" : "text.primary",
                                    bgcolor: a.submenu && submenuOpen ? "neutral.softBg" : "transparent",
                                    "&:hover": { bgcolor: a.danger ? "danger.softBg" : "neutral.softBg" },
                                    transition: "background 0.1s",
                                }}
                            >
                                <Box sx={{ display: "flex", opacity: a.danger ? 1 : 0.6 }}>{a.icon}</Box>
                                <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "inherit", flex: 1 }}>
                                    {a.label}
                                </Typography>
                                {a.submenu && <ChevronRightRoundedIcon sx={{ fontSize: 14, opacity: 0.4 }} />}
                            </Box>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Сабменю — типы задачи */}
            <AnimatePresence>
                {submenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, x: -8 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.92, x: -8 }}
                        transition={{ duration: 0.12 }}
                        style={{
                            position: "fixed",
                            top: submenuPos.top, left: submenuPos.left,
                            zIndex: 10000,
                            background: "var(--joy-palette-background-surface)",
                            borderRadius: 12,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.13)",
                            border: "1px solid var(--joy-palette-neutral-200)",
                            padding: "4px",
                            minWidth: 170,
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {ITEM_TYPES.map((t, i) => (
                            <Box key={i}
                                onClick={() => {
                                    onPatch(item.id, { type: t.value });
                                    setOpen(false);
                                    setSubmenuOpen(false);
                                }}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1.5,
                                    px: 1.5, py: 0.9, borderRadius: 8, cursor: "pointer",
                                    bgcolor: item.type === t.value ? "primary.softBg" : "transparent",
                                    "&:hover": { bgcolor: item.type === t.value ? "primary.softHoverBg" : "neutral.softBg" },
                                    transition: "background 0.1s",
                                }}
                            >
                                <Box sx={{ display: "flex", opacity: 0.6, color: item.type === t.value ? "primary.plainColor" : "inherit" }}>
                                    {t.icon}
                                </Box>
                                <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, flex: 1,
                                    color: item.type === t.value ? "primary.plainColor" : "text.primary" }}>
                                    {t.label}
                                </Typography>
                                {item.type === t.value && <CheckRoundedIcon sx={{ fontSize: 13, color: "primary.plainColor" }} />}
                            </Box>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}

// ─── TaskItem (recursive) ─────────────────────────────────────────────────────
function TaskItem({ item, index, depth = 0, onPatch, onDelete, onAddSubtask, dragHandleProps, reorderMode }) {
    const [hovered, setHovered]         = useState(false);
    const [editing, setEditing]         = useState(!item.text);
    const [showComment, setShowComment] = useState(false);

    const isTouchDevice = typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;

    const [val, setVal] = useState(item.text);
    const isNew = !item.text;

    const inputRef = useRef(null);
    const getInput = () => inputRef.current?.querySelector?.("input") ?? inputRef.current;
    
    useEffect(() => {
        if (editing || item._isNew) {
            // requestAnimationFrame надёжнее setTimeout — ждём реальный paint
            const raf = requestAnimationFrame(() => {
                getInput()?.focus();
            });
            return () => cancelAnimationFrame(raf);
        }
    }, [editing]);

    // При маунте — фокус если новый (isNew = пустой текст, или флаг _isNew)
    useEffect(() => {
        if (isNew || item._isNew) {
            const raf = requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
            return () => cancelAnimationFrame(raf);
        }
    }, []);

    useEffect(() => {
        if (item._startEdit) { setVal(item.text); setEditing(true); onPatch(item.id, { _startEdit: false }); }
    }, [item._startEdit]);

    const totalSubs  = (item.subtasks || []).length;
    const doneSubs   = (item.subtasks || []).filter(s => s.done).length;
    const expanded   = item.expanded ?? false;
    const flameColor = item.flame === "red" ? "#ef4444" : item.flame === "yellow" ? "#f59e0b" : null;

    const commitEdit = (v) => {
        if (v.trim()) {
            // Если есть отложенный реальный id — применяем замену id сейчас
            if (item._realId) {
                onPatch(item.id, { text: v.trim(), id: item._realId, _realId: null, _isNew: false });
            } else {
                onPatch(item.id, { text: v.trim(), _isNew: false });
            }
            setEditing(false);
        }
        else onDelete(item.id);
    };

    return (
        <Box sx={{ pl: depth > 0 ? `${Math.min(depth, 1) * 20}px` : 0 }}>
            {/* Row */}
            <Box
                onMouseEnter={() => { if (!isTouchDevice) setHovered(true); }}
                onMouseLeave={() => { if (!isTouchDevice) setHovered(false); }}
                sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, minHeight: 38,
                    borderRadius: 10, transition: "background 0.12s, box-shadow 0.3s",
                    ...(item.flame
                        ? flameRowSx(item.flame)
                        : { bgcolor: hovered ? "neutral.softBg" : "transparent" }) }}
            >
                {depth > 0 && (
                    <SubdirectoryArrowRightRoundedIcon sx={{ fontSize: 13, color: "text.tertiary", flexShrink: 0 }} />
                )}

                {/* Ручка перетаскивания — только в reorderMode */}
                {reorderMode && dragHandleProps ? (
                    <Box {...dragHandleProps}
                        sx={{ flexShrink: 0, cursor: "grab", color: "text.tertiary", display: "flex",
                            "&:active": { cursor: "grabbing" }, mr: -0.5 }}
                    >
                        <DragIndicatorRoundedIcon sx={{ fontSize: 16 }} />
                    </Box>
                ) : null}

                {totalSubs > 0 && (
                    <Box sx={{ width: 18, height: 18, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <motion.div animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                            style={{ display: "flex", cursor: "pointer" }}
                            onClick={() => onPatch(item.id, { expanded: !expanded })}
                        >
                            <ChevronRightRoundedIcon sx={{ fontSize: 16, color: "text.tertiary" }} />
                        </motion.div>
                    </Box>
                )}

                <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                    <ItemIcon type={item.type} done={item.done} index={index}
                        onToggle={() => onPatch(item.id, { done: !item.done })} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {editing ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
                            <Input ref={inputRef} size="sm" value={val} slotProps={{ input: { 'aria-label': 'Текст задачи' } }}
                                endDecorator={<VoiceInput targetRef={inputRef} onChange={setVal} label="Продиктовать задачу"/>}
                                onChange={e => setVal(e.target.value)}
                                onBlur={(e) => {
                                    if (deferDictationBlur(e) || e.relatedTarget?.dataset?.action) return;
                                    if (!val.trim()) onDelete(item.id);  // пустая — всегда удаляем
                                    else commitEdit(val);
                                }}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === "Escape") stopDictationFor(e.target);
                                    if (e.key === "Enter") commitEdit(val);
                                    if (e.key === "Escape") {
                                        if (isNew || !val.trim()) onDelete(item.id);  // новый или пустой — удаляем
                                        else { setVal(item.text); setEditing(false); } // существующий — откат
                                    }
                                }}
                                sx={{ fontSize: "0.9rem", height: 30, "--Input-focusedHighlight": "transparent",
                                    bgcolor: "transparent", py: 0, "& input": { lineHeight: 1.5 } }}
                            />
                            {/* Подтвердить */}
                            <IconButton size="sm" variant="soft" color="primary"
                                data-action="confirm"
                                sx={{ width: 30, height: 24, minWidth: 0, borderRadius: 10, flexShrink: 0 }}
                                onMouseDown={e => { e.preventDefault(); commitEdit(val); }}
                            ><CheckRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                            {/* Отменить */}
                            <IconButton size="sm" variant="soft" color="danger"
                                data-action="cancel"
                                sx={{ width: 30, height: 24, minWidth: 0, borderRadius: 10, flexShrink: 0 }}
                                onMouseDown={e => {
                                    e.preventDefault();
                                    if (isNew || !val.trim()) onDelete(item.id);  // новый — удаляем
                                    else { setVal(item.text); setEditing(false); } // существующий — откат
                                }}
                            ><CloseRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            <Typography
                                className="ld-text-wrap" onDoubleClick={() => { setVal(item.text); setEditing(true); }}
                                sx={{ fontSize: "0.9rem", lineHeight: 1.5, cursor: "text",
                                    color: item.done ? "text.tertiary" : "text.primary",
                                    textDecoration: item.done ? "line-through" : "none", transition: "all 0.15s" }}
                            ><LinkedText text={item.text}/></Typography>
                            {totalSubs > 0 && (
                                <Chip size="sm" variant="soft" color={doneSubs === totalSubs ? "success" : "neutral"}
                                    onClick={() => onPatch(item.id, { expanded: !expanded })}
                                    sx={{ fontSize: "0.7rem", height: 16, px: 0.7, cursor: "pointer" }}
                                >{doneSubs}/{totalSubs}</Chip>
                            )}
                        </Box>
                    )}
                </Box>
                <Box sx={{ flexShrink: 0, minWidth: hovered && !editing && !isTouchDevice ? 118 : 0, overflow: "visible", transition: "min-width 0.1s", display: { xs: "none", md: "block" } }}>
                    <AnimatePresence>
                        {hovered && !editing && !isTouchDevice && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                transition={{ duration: 0.1 }} style={{ display: "flex", gap: 4, alignItems: "center" }}
                            >
                                <Tooltip title="Комментарий" size="sm">
                                    <IconButton size="sm" variant="plain" color="neutral"
                                        sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                        onClick={() => setShowComment(v => !v)}
                                    ><FormatQuoteRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                                </Tooltip>
                                {depth === 0 && (
                                    <Tooltip title="Подзадача" size="sm">
                                        <IconButton size="sm" variant="plain" color="neutral"
                                            sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                            onClick={() => onAddSubtask(item.id)}
                                        ><SubdirectoryArrowRightRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                                    </Tooltip>
                                )}
                                <FlameButton flame={item.flame} onChange={(v) => onPatch(item.id, { flame: v })} />
                                <TypeMenu type={item.type} onChange={(v) => onPatch(item.id, { type: v })} />

                                <Tooltip title="Удалить" size="sm">
                                    <IconButton size="sm" variant="plain" color="danger"
                                        sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                        onClick={() => confirmDelete(() => onDelete(item.id))}
                                    ><DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} /></IconButton>
                                </Tooltip>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Box>

                <ItemMobileMenu
                    item={item} depth={depth}
                    onPatch={onPatch} onDelete={onDelete}
                    onAddSubtask={onAddSubtask}
                    onComment={() => setShowComment(v => !v)}
                />
            </Box>

            {/* Inline comment editor */}
            <AnimatePresence>
                {showComment && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }}
                        style={{ overflow: "hidden" }}
                    >
                        <Box sx={{ pl: "38px", pr: 1, pb: 1, pt: 0.5 }}>
                            <CommentEditor
                                onSubmit={async ({ text, quoted, files }) => {
                                    // Optimistic — show instantly
                                    const tmp = { id: genId(), text, quoted, createdAt: fmtNow(),
                                        updatedAt: null, authorName: null, attachments: [] };
                                    onPatch(item.id, { comments: [...(item.comments || []), tmp] });
                                    try {
                                        const fd = new FormData();
                                        fd.append("text", text);
                                        fd.append("is_quoted", quoted ? "true" : "false");
                                        (files || []).forEach(f => fd.append("files", f));
                                        const data = await APICreateComment(item.id, fd);
                                        onPatch(item.id, {
                                            comments: [...(item.comments || []), tmp].map(c =>
                                                c.id === tmp.id ? normaliseComment(data) : c
                                            ),
                                        });
                                        setShowComment(false);
                                    } catch (err) {
                                        onPatch(item.id, { comments: item.comments || [] });
                                        throw err;
                                    }
                                }}
                                onCancel={() => setShowComment(false)}
                            />
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Comments list */}
            {(item.comments || []).length > 0 && (
                <Box sx={{ pl: 5, pr: 1 }}>
                    {(item.comments || []).map(c => (
                        <CommentBubble key={c.id} c={c}
                            onDelete={async (cid) => {
                                onPatch(item.id, { comments: (item.comments || []).filter(x => x.id !== cid) });
                                try { await APIDeleteComment(cid); } catch {}
                            }}
                            onSave={async (cid, { text, quoted, files, removeAttachmentIds }) => {
                                // оптимистично обновляем текст/цитату
                                onPatch(item.id, { comments: (item.comments || []).map(x =>
                                    x.id === cid ? { ...x, text, quoted } : x) });
                                try {
                                    const fd = new FormData();
                                    fd.append("text", text);
                                    fd.append("is_quoted", quoted ? "true" : "false");
                                    (files || []).forEach(f => fd.append("files", f));
                                    (removeAttachmentIds || []).forEach(id => fd.append("remove_attachments", id));
                                    const data = await APIUpdateComment(cid, fd);
                                    onPatch(item.id, { comments: (item.comments || []).map(x =>
                                        x.id === cid ? normaliseComment(data) : x) });
                                } catch (err) {
                                    onPatch(item.id, { comments: item.comments || [] });
                                    throw err;
                                }
                            }}
                        />
                    ))}
                </Box>
            )}

            {/* Recursive subtasks */}
            <AnimatePresence>
                {expanded && totalSubs > 0 && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: "hidden" }}
                    >
                        {(item.subtasks || []).map((sub, si) => (
                            <TaskItem key={sub.id} item={sub} index={si} depth={depth + 1}
                                onPatch={onPatch} onDelete={onDelete} onAddSubtask={onAddSubtask}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}

// ─── SectionBlock ─────────────────────────────────────────────────────────────
function SectionBlock({ section, onUpdateSection, onDeleteSection, reorderMode }) {
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleVal, setTitleVal]         = useState(section.title);
    const [hovered, setHovered]           = useState(false);

    const [mutationError, setMutationError] = useState("");
    const titleRef = useRef(null);
    const getTitleInput = () => titleRef.current?.querySelector?.("input") ?? titleRef.current;
    useEffect(() => {
        if (editingTitle) {
            const raf = requestAnimationFrame(() => getTitleInput()?.focus());
            return () => cancelAnimationFrame(raf);
        }
    }, [editingTitle]);

    useEffect(() => {
        if (section._autoEdit && !editingTitle) {
            setEditingTitle(true);
        }
    }, []);


    // ── Item mutations — optimistic + API ────────────────────────────────────
    const patchItem = (itemId, patch) => {
        let newItems = updateItemRec(section.items, itemId, n => ({ ...n, ...patch }));

        // Авто-выполнение родителя если все countable подзадачи выполнены
        if ("done" in patch) {
            newItems = newItems.map(parent => {
                if (!COUNTABLE_TYPES.includes(parent.type)) return parent;
                const subs = (parent.subtasks || []).filter(s => COUNTABLE_TYPES.includes(s.type));
                if (!subs.length) return parent;
                const allDone = subs.every(s => {
                    // учитываем только что изменённую подзадачу
                    if (s.id === itemId) return patch.done;
                    return s.done;
                });
                if (allDone !== parent.done) {
                    APIUpdateItem(parent.id, { done: allDone }).catch(() => {});
                    return { ...parent, done: allDone };
                }
                return parent;
            });
        }

        onUpdateSection({ ...section, items: newItems });
        const apiPatch = itemPatchToApi(patch);
        if (Object.keys(apiPatch).length)
            APIUpdateItem(itemId, apiPatch).catch(() => setMutationError("Не удалось сохранить задачу. Обновите список и повторите."));
    };

    const changeItems = fn => onUpdateSection(s => s.id === section.id ? { ...s, items: fn(s.items) } : s);
    const deleteItem = async (itemId) => {
        try { await APIDeleteItem(itemId); changeItems(items => deleteItemRec(items, itemId)); }
        catch { setMutationError("Не удалось удалить задачу. Повторите."); }
    };
    const addSubtask = async (parentId) => {
        try { const data = await APICreateSubtask(parentId, { item_type: "checkbox", text: "" });
            changeItems(items => addSubtaskRec(items, parentId, { ...normaliseItem(data), _isNew: true }));
        } catch { setMutationError("Не удалось создать подзадачу."); }
    };
    const addItem = async () => {
        try { const data = await APICreateItem(section.id, { item_type: "checkbox", text: "" });
            changeItems(items => [...items, { ...normaliseItem(data), _isNew: true }]);
        } catch { setMutationError("Не удалось создать задачу."); }
    };

    const commitTitle = () => {
        onUpdateSection({ ...section, title: titleVal });
        APIUpdateSection(section.id, { title: titleVal }).catch(() => {});
        setEditingTitle(false);
    };

    return (
        <Box sx={{ mb: 3 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {mutationError && <Typography role="alert" color="danger" level="body-xs">{mutationError}</Typography>}
            {/* Section header */}
            <Box sx={{ display: "flex", minHeight: 35, alignItems: "center", gap: 1, mb: 0.5, pl: 1 }}>
                {editingTitle ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1 }}>
                        <Input ref={titleRef} size="sm" value={titleVal} slotProps={{ input: { 'aria-label': 'Название раздела', maxLength: 200 } }}
                            endDecorator={<VoiceInput targetRef={titleRef} onChange={setTitleVal} maxLength={200} label="Продиктовать название раздела"/>}
                            onChange={e => setTitleVal(e.target.value)}
                            onBlur={e => { if (!deferDictationBlur(e)) commitTitle(); }}
                            onKeyDown={e => { if (e.key === "Enter") commitTitle(); if (e.key === "Escape") setEditingTitle(false); }}
                            sx={{ flex: 1, fontSize: "0.75rem", fontWeight: 700, height: 24, letterSpacing: "0.08em",
                                "--Input-focusedHighlight": "transparent", py: 0 }}
                        />
                        <IconButton size="sm" variant="soft" color="primary"
                            sx={{ width: 30, height: 24, minWidth: 0, borderRadius: 10 }}
                            onMouseDown={e => { e.preventDefault(); commitTitle(); }}
                        ><CheckRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                    </Box>
                ) : (
                    <Typography
                        onDoubleClick={() => setEditingTitle(true)}
                        sx={{ flex: 1, fontSize: "0.9rem", fontWeight: 700, letterSpacing: "0.09em",
                            textTransform: "uppercase", color: "text.tertiary", cursor: "text" }}
                    >{section.title || "Без раздела"}</Typography>
                )}

                <AnimatePresence>
                    {hovered && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            style={{ display: "flex", gap: 2 }}>
                            <Tooltip title="Переименовать" size="sm">
                                <IconButton size="sm" variant="plain" color="neutral"
                                    sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 10, display: { xs: "none", md: "flex" } }}
                                    onClick={() => setEditingTitle(true)}
                                ><DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                            </Tooltip>
                            <Tooltip title="Удалить раздел" size="sm">
                                <IconButton size="sm" variant="plain" color="danger"
                                    sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 10, display: { xs: "none", md: "flex" } }}
                                    onClick={() => confirmDelete(() => onDeleteSection(section.id))}
                                ><DeleteRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                            </Tooltip>
                        </motion.div>
                    )}
                </AnimatePresence>
                {/* Мобайл: всегда видны */}
                <Stack direction="row" spacing={0.25} sx={{ display: { xs: "flex", md: "none" } }}>
                    <IconButton size="sm" variant="plain" color="neutral"
                        sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 10, opacity: 0.5 }}
                        onClick={() => setEditingTitle(true)}
                    ><DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                    <IconButton size="sm" variant="plain" color="danger"
                        sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 10, opacity: 0.5 }}
                        onClick={() => confirmDelete(() => onDeleteSection(section.id))}
                    ><DeleteRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                </Stack>

            </Box>
            <Divider sx={{ mb: 0.75, opacity: 0.5 }} />

            {section.items.map((item, idx) => <TaskItem key={item.id} item={item} index={idx} depth={0} onPatch={patchItem} onDelete={deleteItem} onAddSubtask={addSubtask}/>)}

            <Box onClick={addItem}
                sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.5, borderRadius: 10,
                    cursor: "pointer", mt: 0.25,
                    "&:hover": { bgcolor: "neutral.softBg", color: "primary.plainColor" }, transition: "all 0.12s" }}
            >
                <AddRoundedIcon sx={{ fontSize: 15, color: "primary.600" }} />
                <Typography sx={{ fontSize: "0.83rem", color: "primary.600" }}>Добавить задачу</Typography>
            </Box>
        </Box>
    );
}

// ─── TaskEditor ───────────────────────────────────────────────────────────────
function TaskEditor({ task, onUpdate, setFolderSidebarOpen }) {
    const [titleEditing, setTitleEditing] = useState(false);
    const [reorderMode, setReorderMode]   = useState(false);
    const [arrangeState, setArrangeState] = useState("");
    const arrangeBusy = useRef(false);

    const [refreshing, setRefreshing] = useState(false);


    const scrollRef = useRef(null);

    const titleInputRef = useRef(null);
    const getTitleInput = () => titleInputRef.current?.querySelector?.("input") ?? titleInputRef.current;

    useEffect(() => {
        if (titleEditing) {
            const raf = requestAnimationFrame(() => getTitleInput()?.focus());
            return () => cancelAnimationFrame(raf);
        }
    }, [titleEditing]);

    const taskRef = useRef(task);
    useEffect(() => { taskRef.current = task; }, [task]);
    
    const handleRefreshList = useCallback(async () => {
        setRefreshing(true);
        try {
            const currentTask = taskRef.current;
            if (!currentTask) return;
            const data = await APIGetTaskList(currentTask.id);
            onUpdate(currentTask.id, normaliseList(data));
        } catch {}
        finally { setRefreshing(false); }
    }, [onUpdate]);

    const arrange = async (nodes) => {
        if (arrangeBusy.current) return;
        const previous = taskRef.current;
        const fromItem = ({ children, ...rest }) => ({ ...rest, subtasks: (children || []).map(fromItem) });
        const sections = nodes.map(({ children, ...rest }) => ({ ...rest, items: (children || []).map(fromItem) }));
        arrangeBusy.current = true; setArrangeState("Сохраняем порядок…");
        onUpdate(previous.id, { sections });
        try {
            const placement = item => ({ id: item.id, subtasks: (item.subtasks || []).map(placement) });
            await api.post(`/tasks/lists/${previous.id}/arrange/`, { sections: sections.map(section => ({ id: section.id, items: section.items.map(placement) })) });
            setArrangeState("Порядок сохранён");
        } catch (e) {
            onUpdate(previous.id, { sections: previous.sections });
            setArrangeState(e.response?.data?.error || "Не удалось сохранить порядок. Повторите.");
        } finally { arrangeBusy.current = false; }
    };

    if (!task) {
        return (
            <Box component={motion.div}
                initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2 }} exit={{ opacity: 0, y: 15 }}
                sx={{ flex: 1, display: "flex", flexDirection: "column", height: "-webkit-fill-available" }}
            >
                {/* Кнопка папок на мобайле — всегда видна */}
                <Box sx={{ display: { xs: "flex", md: "none" }, flexShrink: 0 }}>
                    <Button
                        size="sm" variant="soft" color="primary"
                        onClick={() => setFolderSidebarOpen(true)}
                        sx={{ borderRadius: 15, width: 100, height: 43 }}
                        startDecorator={<FolderRoundedIcon sx={{ fontSize: 20 }} />}
                    >
                        Задачи
                    </Button>
                </Box>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} style={{ textAlign: "center" }}>
                        <AssignmentRoundedIcon sx={{ fontSize: 60, opacity: 0.13, mb: 2 }} />
                        <Typography level="body-lg" sx={{ color: "text.tertiary", fontWeight: 500 }}>Выберите список задач</Typography>
                        <Typography level="body-sm" sx={{ color: "text.tertiary", mt: 0.5 }}>или создайте новый в боковой панели</Typography>
                    </motion.div>
                </Box>
            </Box>
        );
    }

    const updateSection = (updatedOrFn) => {
        onUpdate(task.id, current => ({ sections: current.sections.map(s =>
            typeof updatedOrFn === 'function' ? (updatedOrFn(s) ?? s) : s.id === updatedOrFn.id ? updatedOrFn : s) }));
    };

    const deleteSection = (secId) => {
        onUpdate(task.id, { sections: task.sections.filter(s => s.id !== secId) });
        APIDeleteSection(secId).catch(() => {});
    };

    const addSection = async () => {
        try { const data = await APICreateSection(task.id, { title: "" });
            onUpdate(task.id, current => ({ sections: [...current.sections, { ...normaliseSection(data), _autoEdit: true }] }));
        } catch { setArrangeState("Не удалось создать раздел."); }
    };

    const commitTitle = (name) => {
        onUpdate(task.id, { name });
        APIUpdateTaskList(task.id, { name }).catch(() => {});
        setTitleEditing(false);
    };

    const allCountable = task.sections.flatMap(s => collectCountable(s.items));
    const totalItems   = allCountable.length;
    const doneItems    = allCountable.filter(i => i.done).length;
    const progress     = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;

    return (
        <Box component={motion.div} key={task.id}
            initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }} exit={{ opacity: 0, y: 15 }}
            sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}
        >
            <Stack spacing={0.5} direction={'row'} sx={{
                display: "flex", alignItems: "center",
                justifyContent: "space-between",
                gap: 0.5, ml: { xs: 0, md: 1 },
                mb: 1, borderRadius: 20,
                flexShrink: 0,
            }}>
                {/* Кнопка папок — только мобайл, внутри toolbar */}
                <Box sx={{ display: { xs: "flex", md: "none" }, flexShrink: 0 }}>
                    <Button
                        size="sm" variant="soft" color="primary"
                        onClick={() => setFolderSidebarOpen(true)}
                        sx={{ borderRadius: 15, width: 100, height: 43 }}
                        startDecorator={<FolderRoundedIcon sx={{ fontSize: 20 }} />}
                    >
                        Задачи
                    </Button>
                </Box>

                <Sheet sx={{
                    display: "flex", alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1, px: 1, ml: { xs: 0, md: 1 },
                    py: 0.8, mb: 1, borderRadius: 20, width: '100%',
                    bgcolor: 'neutral.100'
                }}>
                    {/* Кнопка режима перемещения */}
                    <Tooltip title={reorderMode ? "Выйти из режима" : "Изменить порядок"} size="sm">
                        <IconButton
                            size="sm"
                            variant={reorderMode ? "soft" : "plain"}
                            color={reorderMode ? "primary" : "neutral"}
                            onClick={() => setReorderMode(v => !v)}
                            sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 8 }}
                        >
                            {reorderMode
                                ? <CheckRoundedIcon sx={{ fontSize: 17 }} />
                                : <LowPriorityRoundedIcon sx={{ fontSize: 17 }} />
                            }
                        </IconButton>
                    </Tooltip>

                    {/* Кнопка редактирования порядка */}
                    <Tooltip title="Обновить" size="sm">
                        <IconButton size="sm" variant="plain" color="neutral"
                            sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 8 }}
                            onClick={handleRefreshList}
                            disabled={refreshing}
                        >
                            <motion.div
                                animate={{ rotate: refreshing ? 360 : 0 }}
                                transition={refreshing ? { duration: 0.8, repeat: Infinity, ease: "linear" } : { duration: 0 }}
                                style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                                <RefreshRoundedIcon sx={{ fontSize: 17 }} />
                            </motion.div>
                        </IconButton>
                    </Tooltip>

                    {/* Прогресс — справа (или по центру на десктопе) */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                        {/* {totalItems > 0 && ( */}
                        <>
                            <Box sx={{ width: 80, height: 6, bgcolor: "neutral.200", borderRadius: 4, overflow: "hidden" }}>
                                <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }}
                                    transition={{ duration: 0.5, ease: "easeOut" }}
                                    style={{ height: "100%", background: progress === 100 ? "#22c55e" : "#572AC3", borderRadius: 4 }}
                                />
                            </Box>
                            <Typography sx={{ fontSize: "0.77rem", color: "text.tertiary", minWidth: 32 }}>{progress}%</Typography>
                        </>
                        {/* )} */}
                    </Box>
                </Sheet>
            </Stack>

            {/* Scrollable content */}
            <Box ref={scrollRef} sx={{ flex: 1, overflow: "auto", px: { xs: 2, md: 4 }, pb: 8 }}>
                <Box sx={{ px: { xs: 0, md: 4 }, pt: 3, mb: 3 }}>
                    {titleEditing ? (
                        <Box>
                            <Input ref={titleInputRef} value={task.name} slotProps={{ input: { 'aria-label': 'Название списка задач', maxLength: 200 } }}
                                endDecorator={<VoiceInput targetRef={titleInputRef} onChange={name => onUpdate(task.id, { name })} maxLength={200} label="Продиктовать название списка"/>}
                                onChange={e => onUpdate(task.id, { name: e.target.value })}
                                onBlur={(e) => {
                                    if (deferDictationBlur(e) || e.relatedTarget?.dataset?.titleAction) return;
                                    commitTitle(task.name);
                                }}
                                onKeyDown={e => {
                                    if (e.key === "Enter" || e.key === "Escape") stopDictationFor(e.target);
                                    if (e.key === "Enter") commitTitle(task.name);
                                    if (e.key === "Escape") setTitleEditing(false);
                                }}
                                sx={{ fontSize: "clamp(1.3rem, 3vw, 1.9rem)", fontWeight: 800, border: "none", boxShadow: "none",
                                    bgcolor: "transparent", p: 0, "--Input-focusedHighlight": "transparent",
                                    borderBottom: "2px solid var(--joy-palette-primary-200)", borderRadius: 0, mb: 1 }}
                            />
                            {/* Кнопки подтверждения — всегда видны на мобайле, на десктопе тоже */}
                            <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                                <Button
                                    size="sm" variant="soft" color="primary"
                                    data-title-action="confirm"
                                    onMouseDown={e => { e.preventDefault(); commitTitle(task.name); }}
                                    startDecorator={<CheckRoundedIcon sx={{ fontSize: 14 }} />}
                                    sx={{ borderRadius: 10, height: 30, px: 1.5, fontSize: "0.78rem" }}
                                >
                                    Сохранить
                                </Button>
                                <Button
                                    size="sm" variant="soft" color="neutral"
                                    data-title-action="cancel"
                                    onMouseDown={e => { e.preventDefault(); setTitleEditing(false); }}
                                    startDecorator={<CloseRoundedIcon sx={{ fontSize: 14 }} />}
                                    sx={{ borderRadius: 10, height: 30, px: 1.5, fontSize: "0.78rem" }}
                                >
                                    Отмена
                                </Button>
                            </Stack>
                        </Box>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                            <Typography onClick={() => setTitleEditing(true)}
                                sx={{ fontSize: "clamp(1.5rem, 3vw, 1.9rem)", fontWeight: 800, lineHeight: 1.1,
                                    letterSpacing: "-0.5px", color: "text.primary", cursor: "text",
                                    "&:hover": { color: "primary.plainColor" }, transition: "color 0.15s" }}
                            >{task.name}</Typography>
                            {/* Кнопка редактирования — только мобайл */}
                            <IconButton
                                size="sm" variant="plain" color="neutral"
                                onClick={() => setTitleEditing(true)}
                                sx={{
                                    display: { xs: "flex", md: "none" },
                                    width: 30, height: 30, minWidth: 0,
                                    borderRadius: 8, opacity: 0.45, flexShrink: 0,
                                }}
                            >
                                <DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>
                    )}
                    <Typography sx={{ fontSize: "0.81rem", color: "text.tertiary" }}>
                        {doneItems} из {totalItems} задач выполнено
                    </Typography>
                </Box>

                <Box sx={{ px: { xs: 0, md: 4 } }}>
                    {arrangeState && <Typography role="status" level="body-xs" sx={{ mb: 1, color: arrangeState.includes("Не удалось") ? "danger.500" : "text.tertiary" }}>{arrangeState}</Typography>}
                    {reorderMode ? <ReorderTree
                        tree={task.sections.map(sec => ({ ...sec, type: 'section', name: sec.title || 'Без раздела', children: (function convert(items) { return items.map(it => ({ ...it, name: it.text || '(пустая задача)', children: convert(it.subtasks || []) })); })(sec.items) }))}
                        disabled={arrangeBusy.current} onChange={arrange}
                        canRoot={node => node.type === 'section'}
                        canContain={(parent, node) => node.type !== 'section' && (parent.type === 'section' || !!parent.subtasks)}
                        render={node => <Typography className="ld-text-wrap" sx={{ fontWeight: node.type === 'section' ? 700 : 400, fontSize: '.9rem', flex: 1, py: .5 }}>{node.name}</Typography>}
                    /> : task.sections.map(sec => <SectionBlock key={sec.id} section={sec} onUpdateSection={updateSection} onDeleteSection={deleteSection} reorderMode={false}/>)}

                    {/* Кнопка добавления раздела */}
                    <Box onClick={addSection}
                        sx={{
                            display: "flex", alignItems: "center", gap: 0.5,
                            px: 1.5, py: 1, mt: 1, borderRadius: 12,
                            cursor: "pointer", color: "text.tertiary",
                            border: "1.5px dashed", borderColor: "neutral.300",
                            "&:hover": { bgcolor: "neutral.softBg", color: "primary.plainColor", borderColor: "primary.300" },
                            transition: "all 0.15s",
                        }}
                    >
                        <AddRoundedIcon sx={{ fontSize: 16, color: "primary.600" }} />
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 500, color: "primary.600" }}>Добавить раздел</Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

// ─── NodeMenuButton ───────────────────────────────────────────────────────────
function NodeMenuButton({ node, onAddFolder, onAddTaskList, onDelete, onRename, onShare }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos]   = useState({ top: 0, left: 0 });
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [open]);

    const actions = [
        ...(node.type === "folder" ? [
            { label: "Новая папка",      icon: <CreateNewFolderRoundedIcon sx={{ fontSize: 15 }} />, onClick: () => onAddFolder(node.id) },
            { label: "Новый список",     icon: <AddTaskRoundedIcon sx={{ fontSize: 15 }} />,         onClick: () => onAddTaskList(node.id) },
        ] : []),
        { label: "Переименовать",    icon: <DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 15 }} />, onClick: () => onRename() },
        { label: "Поделиться", icon: <ShareRoundedIcon sx={{ fontSize: 15 }} />, onClick: () => onShare?.(node) },
        { label: "Удалить",          icon: <DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} />,          onClick: () => confirmDelete(() => onDelete(node.id)), danger: true },
    ];

    return (
        <Box ref={ref} sx={{ position: "relative", flexShrink: 0 }}>
            <IconButton
                size="sm" variant="plain" color="neutral"
                sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                onMouseDown={e => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    setPos({ top: rect.bottom + 4, left: rect.right - 160 });
                    setOpen(v => !v);
                }}
            >
                <MoreHorizRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92, y: -4 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.92, y: -4 }}
                        transition={{ duration: 0.12 }}
                        style={{
                            position: "fixed",
                            top: pos.top, left: pos.left,
                            zIndex: 9999,
                            background: "var(--joy-palette-background-surface)",
                            borderRadius: 12,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.13)",
                            border: "1px solid var(--joy-palette-neutral-200)",
                            padding: "4px",
                            minWidth: 160,
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {actions.map((a, i) => (
                            <Box key={i}
                                onClick={() => { a.onClick(); setOpen(false); }}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1,
                                    px: 1.5, py: 0.75, borderRadius: 8, cursor: "pointer",
                                    color: a.danger ? "danger.plainColor" : "text.primary",
                                    "&:hover": { bgcolor: a.danger ? "danger.softBg" : "neutral.softBg" },
                                    transition: "background 0.1s",
                                }}
                            >
                                <Box sx={{ color: "inherit", display: "flex" }}>{a.icon}</Box>
                                <Typography sx={{ fontSize: "0.83rem", fontWeight: 500, color: "inherit" }}>
                                    {a.label}
                                </Typography>
                            </Box>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}

// ─── TreeNode ─────────────────────────────────────────────────────────────────
function TreeNode({ node, depth = 0, activeId, onSelect, onAddFolder, onAddTaskList, onDelete, onToggle, onRename, dragHandleProps, editMode, onShare }) {
    const [hovered, setHovered] = useState(false);
    const [editing, setEditing] = useState(false);

    const isTouchDevice = typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;

    const [editVal, setEditVal] = useState(node.name);
    const inputRef = useRef(null);
    const isActive = activeId === node.id;
    useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

    function submit() { if (editVal.trim()) onRename(node.id, editVal.trim()); setEditing(false); }

    return (
        <Box>
            <Box
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{ display: "flex", alignItems: "center", pl: `${10 + depth * 18}px`, pr: 1,
                    py: 0, my: 0.2, borderRadius: 10, cursor: "pointer",
                    bgcolor: isActive ? "primary.softBg" : hovered ? "neutral.softBg" : "transparent",
                    transition: "background 0.12s", gap: 0.75, minHeight: 36 }}
                onClick={() => node.type === "folder" ? onToggle(node.id) : onSelect(node.id)}
            >
                {node.type === "folder" ? (
                    <motion.div animate={{ rotate: node.open ? 90 : 0 }} transition={{ duration: 0.15 }}
                        style={{ display: "flex", flexShrink: 0 }}>
                        <ChevronRightRoundedIcon sx={{ fontSize: 17, color: "text.tertiary" }} />
                    </motion.div>
                ) : (
                    <Box sx={{ width: 17, flexShrink: 0 }} />
                )}
                {editMode && dragHandleProps ? (
                    <Box {...dragHandleProps} sx={{ flexShrink: 0, cursor: "grab", color: "text.tertiary", display: "flex", "&:active": { cursor: "grabbing" } }}>
                        <DragIndicatorRoundedIcon sx={{ fontSize: 18 }} />
                    </Box>
                ) : (
                    <Box sx={{ flexShrink: 0, height: '1.2em' }}>
                        {node.type === "folder"
                            ? node.open
                                ? <FolderOpenRoundedIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                                : <FolderRoundedIcon    sx={{ fontSize: 18, color: "#f59e0b" }} />
                            : <AssignmentRoundedIcon sx={{ fontSize: 17, color: isActive ? "primary.plainColor" : "text.tertiary" }} />
                        }
                    </Box>
                )}
                {editing ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, width: 10 }}>
                        <Input ref={inputRef} size="sm" value={editVal} slotProps={{ input: { 'aria-label': 'Название элемента дерева', maxLength: 200 } }}
                            endDecorator={<VoiceInput targetRef={inputRef} onChange={setEditVal} maxLength={200} label="Продиктовать название"/>}
                            onChange={e => setEditVal(e.target.value)}
                            onBlur={e => { if (!deferDictationBlur(e)) submit(); }}
                            onKeyDown={e => { if (e.key === "Enter") submit(); if (e.key === "Escape") setEditing(false); }}
                            sx={{ flex: 1, fontSize: "0.88rem", py: 0, px: 0.5, height: 28, "--Input-focusedHighlight": "transparent" }}
                            onClick={e => e.stopPropagation()}
                        />
                        <IconButton size="sm" variant="solid" color="success"
                            sx={{ width: 30, height: 24, minWidth: 0, borderRadius: 10 }}
                            onMouseDown={() => submit()}
                        ><CheckRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                    </Box>
                ) : (
                    <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 0.75, overflow: "hidden" }}>
                        <Typography onDoubleClick={e => { e.stopPropagation(); setEditing(true); }}
                            sx={{ fontWeight: isActive ? 600 : 400, color: isActive ? "primary.plainColor" : "text.primary",
                                fontSize: "0.88rem", userSelect: "none",
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >{node.name}</Typography>
                    </Box>
                )}
                <AnimatePresence>
                    {hovered && !editing && !isTouchDevice && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            style={{ display: "flex", gap: 2, flexShrink: 0 }}
                            onClick={e => e.stopPropagation()}
                        >
                            {node.type === "folder" && (
                                <>
                                    <Tooltip title="Новая папка" size="sm">
                                        <IconButton size="sm" variant="plain" color="neutral"
                                            sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                            onClick={() => onAddFolder(node.id)}
                                        ><CreateNewFolderRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Новый список" size="sm">
                                        <IconButton size="sm" variant="plain" color="neutral"
                                            sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                            onClick={() => onAddTaskList(node.id)}
                                        ><AddTaskRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                                    </Tooltip>
                                </>
                            )}

                            <Tooltip title="Поделиться" size="sm">
                                <IconButton size="sm" variant="plain" color="neutral"
                                    sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                    onClick={() => onShare?.(node)}
                                ><ShareRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                            </Tooltip>
                            <Tooltip title="Удалить" size="sm">
                                <IconButton size="sm" variant="plain" color="danger"
                                    sx={{ width: 26, height: 26, minWidth: 0, borderRadius: 7 }}
                                    onClick={() => confirmDelete(() => onDelete(node.id))}
                                ><DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} /></IconButton>
                            </Tooltip>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Мобайл: кнопка ⋯ всегда видна */}
                {isTouchDevice && !editing && (
                    <Box onClick={e => e.stopPropagation()}>
                        <NodeMenuButton
                            node={node}
                            onAddFolder={onAddFolder}
                            onAddTaskList={onAddTaskList}
                            onDelete={onDelete}
                            onRename={() => setEditing(true)}
                            onShare={onShare}
                        />
                    </Box>
                )}
            </Box>
            {node.type === "folder" && (
                <AnimatePresence initial={false}>
                    {node.open && (
                        <motion.div key="ch"
                            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                            style={{ overflow: "hidden" }}
                        >

                                {(node.children || []).map(child => (
                                    <TreeNode key={child.id} node={child} depth={depth + 1}
                                        activeId={activeId} onSelect={onSelect}
                                        onAddFolder={onAddFolder} onAddTaskList={onAddTaskList}
                                        onDelete={onDelete} onToggle={onToggle} onRename={onRename}
                                        onShare={onShare}
                                    />
                                ))}

                        </motion.div>
                    )}
                </AnimatePresence>
            )}
        </Box>
    );
}

// ─── TasksSidebar ─────────────────────────────────────────────────────────────
function TasksSidebar({ tree, activeId, onSelect, onChange, mobileOpen, onMobileClose, onWidthChange, onRefresh, refreshing }) {
    const { setShareModal } = useContext(LayoutContext);
    const [editMode, setEditMode] = useState(false);
    const sidebarScrollRef = useRef(null);

    const { panelRef, width: sidebarWidth, resizing, separatorProps } = usePanelWidth({
        storageKey: 'lunadesk.panels.tasks.width', defaultWidth: 260,
        min: 260, max: 480, breakpoint: 900,
    });
    useEffect(() => { onWidthChange?.(sidebarWidth); }, [sidebarWidth, onWidthChange]);

    const toggle = (id, force) => onChange(p => updateNode(p, id, n => ({ ...n, open: typeof force === 'boolean' ? force : !n.open })));

    const handleSelect = (id) => {
        onSelect(id);
        onMobileClose();
    };

    const handleShare = (node) => {
        setShareModal({
            open: true,
            targetType: node.type === "folder" ? "folder" : "tasklist",
            targetId:   node.id,
            targetName: node.name,
        });
    };

    const del = (id) => {
        const node = findNode(tree, id);
        onChange(p => deleteNode(p, id));
        if (!node) return;
        if (node.type === "folder") APIDeleteFolder(id).catch(() => {});
        else                        APIDeleteTaskList(id).catch(() => {});
    };

    const rename = (id, name) => {
        const node = findNode(tree, id);
        onChange(p => updateNode(p, id, n => ({ ...n, name })));
        if (!node) return;
        if (node.type === "folder") APIUpdateFolder(id, { name }).catch(() => {});
        else                        APIUpdateTaskList(id, { name }).catch(() => {});
    };

    const addFolder = (parentId) => {
        APICreateFolder({ name: "Новая папка" })
            .then(data => {
                const newNode = normaliseFolder(data);
                onChange(p => addToNode(p, parentId, newNode));
            })
            .catch(() => {});
    };

    const addTaskList = (folderId) => {
        APICreateTaskList(folderId, { name: "Новый список", priority: "medium" })
            .then(data => {
                const newNode = normaliseList(data);
                onChange(p => addToNode(p, folderId, newNode));
                onSelect(data.id);
            })
            .catch(() => {});
    };

    const handleAddTaskListFromHeader = () => {
        APICreateTaskListRoot({ name: "Новый список", priority: "medium" })
            .then(data => {
                const newNode = normaliseList(data);
                onChange(p => [...p, newNode]);
                onSelect(data.id);
            })
            .catch(() => {});
    };

    const [orderStatus, setOrderStatus] = useState('');
    const orderBusy = useRef(false);
    const saveOrder = async (newTree) => {
        if (orderBusy.current) return;
        const previous = tree;
        const nodes = [];
        const walk = (items, folder_id = null) => items.forEach((node, order) => {
            nodes.push({ id: node.id, type: node.type, folder_id, order });
            if (node.type === 'folder') walk(node.children || [], node.id);
        });
        walk(newTree); orderBusy.current = true;
        setOrderStatus('Сохраняем порядок…'); onChange(() => newTree);
        try { await APIReorderTree({ nodes }); setOrderStatus('Порядок сохранён'); }
        catch (e) { onChange(() => previous); setOrderStatus(e.response?.data?.error || 'Не удалось сохранить порядок. Повторите.'); }
        finally { orderBusy.current = false; }
    };

    return (
        <Sheet ref={panelRef} data-panel="tasks-navigation" variant="solid" sx={{
            mr: { xs: 0, md: 2 },
            p: 1, pr: { xs: 1, md: 1.5 }, borderRadius: 20,
            // Десктоп — динамическая ширина, мобайл — почти весь экран минус место для кнопки закрытия
            width: { xs: "calc(100vw - 62px)", md: sidebarWidth },
            minWidth: { xs: "calc(100vw - 62px)", md: sidebarWidth },
            maxWidth: { xs: "calc(100vw - 62px)", md: sidebarWidth },
            overflow: "hidden", minHeight: 0,
            height: { xs: "-webkit-fill-available", md: "-webkit-fill-available" },
            display: "flex", flexDirection: "column",
            bgcolor: "neutral.100", flexShrink: 0,
            position: { xs: "fixed", md: "relative" },
            top: { xs: 0, md: "auto" }, left: { xs: 0, md: "auto" },
            zIndex: { xs: 200, md: "auto" },
            m: { xs: 1, md: 0 },
            transform: {
                xs: mobileOpen ? "translateX(0)" : "translateX(calc(-100% - 16px))",
                md: "none",
            },
            transition: resizing ? "none" : mobileOpen
                ? "transform 0.35s cubic-bezier(0.4,0,0.2,1)"
                : "transform 0.35s cubic-bezier(0.4,0,0.2,1) 0.15s",
            boxShadow: { xs: mobileOpen ? "lg" : "none", md: "none" },
            userSelect: resizing ? "none" : "auto",
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: "neutral.50", boxShadow: 'md', borderRadius: 15, px: 2, pt: 1, pb: 1,
                display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                <Typography noWrap level="title-lg" sx={{ fontWeight: 700, fontSize: '.95rem', flexShrink: 0, color: "text.tertiary" }}>Задачи</Typography>
                <Stack direction="row" spacing={0.5}>
                    {/* Кнопка редактирования порядка */}
                    <Tooltip title={editMode ? "Готово" : "Изменить порядок"} size="sm">
                        <IconButton size="sm" variant={editMode ? "soft" : "plain"}
                            color={editMode ? "primary" : "neutral"}
                            sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 8 }}
                            onClick={() => setEditMode(v => !v)}
                        >
                            {editMode
                                ? <CheckRoundedIcon sx={{ fontSize: 17 }} />
                                : <LowPriorityRoundedIcon sx={{ fontSize: 17 }} />
                            }
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Обновить" size="sm">
                        <IconButton size="sm" variant="plain" color="neutral"
                            sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 8 }}
                            onClick={onRefresh}
                            disabled={refreshing}
                        >
                            <motion.div
                                animate={{ rotate: refreshing ? 360 : 0 }}
                                transition={refreshing ? { duration: 0.8, repeat: Infinity, ease: "linear" } : { duration: 0 }}
                                style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
                            >
                                <RefreshRoundedIcon sx={{ fontSize: 17 }} />
                            </motion.div>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Новая папка" size="sm">
                        <IconButton size="sm" variant="plain" color="neutral"
                            sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 8 }}
                            onClick={() => addFolder("root")}
                        ><CreateNewFolderRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
                    </Tooltip>
                    <Tooltip title="Новый список задач" size="sm">
                        <IconButton size="sm" variant="plain" color="neutral"
                            sx={{ width: 30, height: 30, minWidth: 0, borderRadius: 8 }}
                            onClick={handleAddTaskListFromHeader}
                        ><AddTaskRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            {/* Tree */}
            <Box ref={sidebarScrollRef} data-scroll="tasks-navigation" sx={{ flex: 1, minHeight: 0, overflow: "auto", py: 1, px: 0.75,
                "& .sortable-item": {
                    touchAction: editMode ? "none" : "auto",
                }
            }}>
                {orderStatus && <Typography role="status" level="body-xs" sx={{ px: 1, py: .5 }}>{orderStatus}</Typography>}
                {editMode ? <ReorderTree tree={tree} onChange={saveOrder} disabled={orderBusy.current} isExpanded={node => node.open !== false} onToggle={toggle}
                    canContain={(parent, node) => parent.type === 'folder' && node.type === 'tasklist'}
                    render={node => <><Box sx={{ color: 'primary.400', display: 'flex' }}>{node.type === 'folder' ? <FolderRoundedIcon sx={{ fontSize: 19 }}/> : <AssignmentRoundedIcon sx={{ fontSize: 18 }}/>}</Box><Typography level="body-sm" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{node.name}</Typography></>}
                /> : tree.map(node => <TreeNode key={node.id} node={node} activeId={activeId} onSelect={handleSelect}
                    onAddFolder={addFolder} onAddTaskList={addTaskList} onDelete={del} onToggle={toggle} onRename={rename} onShare={handleShare}/>)}
            </Box>

            <PanelResizeHandle label="Ширина панели задач" bindings={separatorProps} />
        </Sheet>
    );
}

// ─── TasksPage ────────────────────────────────────────────────────────────────
export default function TasksPage() {
    const { profile: taskOwner } = useContext(LayoutContext);
    const folderStateKey = folderStorageKey(String(taskOwner?.uuid || taskOwner?.id || "account"));
    const treeStorageOwner = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const listId = searchParams.get("task");
    const [tree, setTree]       = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);
    const [sidebarW, setSidebarW] = useState(256);

    const [folderSidebarOpen, setFolderSidebarOpen] = useState(false);

    const listIdRef = useRef(listId);
    
    const [refreshing, setRefreshing] = useState(false);

    const windowWidth = useWindowWidth();
    const MD_BREAKPOINT = 900;

    // При расширении экрана до десктопа — закрываем мобайл-панель
    useEffect(() => {
        if (windowWidth >= MD_BREAKPOINT) {
            setFolderSidebarOpen(false);
        }
    }, [windowWidth]);


    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');
    const [listRevision, setListRevision] = useState(0);
    const treeController = useRef(null);
    useEffect(() => { listIdRef.current = listId; }, [listId]);
    useEffect(() => () => treeController.current?.abort(), []);

    const loadFolders = useCallback(async (refresh = false) => {
        treeController.current?.abort();
        const controller = new AbortController(); treeController.current = controller;
        if (refresh) setRefreshing(true); else setLoading(true);
        setError(null);
        try {
            const data = await APIGetFolders({ summary: true, signal: controller.signal });
            if (controller.signal.aborted) return;
            const normalised = (data || []).map(item => item?.type === 'folder' ? normaliseFolder(item) : normaliseList(item));
            treeStorageOwner.current = folderStateKey;
            setTree(applyFolderState(normalised, readFolderState(folderStateKey)));
            const initialId = listIdRef.current;
            setActiveId(initialId && findNode(normalised, initialId) ? initialId : null);
            setListRevision(v => v + 1);
        } catch (err) {
            if (!controller.signal.aborted) setError('Не удалось загрузить списки задач');
        } finally {
            if (!controller.signal.aborted) { setLoading(false); setRefreshing(false); }
        }
    }, [folderStateKey]);
    useEffect(() => {
        if (treeStorageOwner.current === folderStateKey) saveFolderState(folderStateKey, tree);
    }, [tree, folderStateKey]);
    const handleRefresh = useCallback(() => loadFolders(true), [loadFolders]);
    useEffect(() => { loadFolders(); }, [loadFolders]);

    // URL is the source of truth for mouse/browser Back and Forward.
    useEffect(() => {
        if (!loading) setActiveId(listId && findNode(tree, listId) ? listId : null);
        // Tree edits must not reset temporary selections during optimistic creation.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listId, loading]);
    const handleSelect = useCallback((id) => {
        setActiveId(id);
        setFolderSidebarOpen(false);
        if (id && id !== listIdRef.current && !String(id).startsWith('tmp-')) setSearchParams({ task: id });
    }, [setSearchParams]);
    const activeNode = activeId ? findNode(tree, activeId) : null;
    const task = activeNode?.type === 'tasklist' ? activeNode : null;
    const needsLoad = !!task && task._loaded === false;
    useEffect(() => {
        setListError('');
        if (!needsLoad || !activeId || String(activeId).startsWith('tmp-')) { setListLoading(false); return; }
        const controller = new AbortController(); setListLoading(true);
        APIGetTaskList(activeId, controller.signal).then(raw => {
            if (controller.signal.aborted) return;
            setTree(prev => updateNode(prev, activeId, node => ({ ...node, ...normaliseList(raw) })));
        }).catch(err => {
            if (!controller.signal.aborted) setListError('Не удалось загрузить выбранный список');
        }).finally(() => { if (!controller.signal.aborted) setListLoading(false); });
        return () => controller.abort();
    }, [activeId, needsLoad, listRevision]);

    const handleUpdate = useCallback((id, patch) => {
        setTree(p => updateNode(p, id, n => ({ ...n, ...(typeof patch === "function" ? patch(n) : patch) })));
    }, []);

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                <CircularProgress size="sm" />
                <Typography sx={{ color: "text.tertiary", fontSize: "0.9rem" }}>Загрузка задач...</Typography>
            </Box>
        );
    }

    // ── Error ─────────────────────────────────────────────────────────────────
    if (error) {
        return (
            <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2 }}>
                <Typography sx={{ color: "danger.500" }}>{error}</Typography>
                <Button size="sm" variant="outlined" startDecorator={<RefreshRoundedIcon />} onClick={() => loadFolders()}>
                    Попробовать снова
                </Button>
            </Box>
        );
    }

    return (
        <Box className="ld-tasks-page" sx={{ display: "flex", minWidth: 0, height: "100%", overflow: "hidden" }}>
            {/* Кнопка открытия папок — только мобайл */}
            <AnimatePresence>
                {folderSidebarOpen && (
                    <motion.div
                        key="folder-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => setFolderSidebarOpen(false)}
                        style={{
                            position: "fixed", inset: 0, zIndex: 199,
                            backdropFilter: "blur(4px)",
                            backgroundColor: "rgba(0,0,0,0.25)",
                        }}
                    />
                )}
            </AnimatePresence>
            
            <TasksSidebar
                tree={tree} activeId={activeId} onSelect={handleSelect} onChange={setTree}
                mobileOpen={folderSidebarOpen} onMobileClose={() => setFolderSidebarOpen(false)}
                onWidthChange={setSidebarW}
                onRefresh={handleRefresh}
                refreshing={refreshing}
            />

            {/* Круглая кнопка закрытия — справа от панели, только мобайл */}
            <AnimatePresence>
                {folderSidebarOpen && (
                    <motion.div
                        key="close-btn"
                        initial={{ x: -44, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -44, opacity: 0 }}
                        transition={{ duration: 0.25, delay: 0.22, ease: [0.34, 1.56, 0.64, 1] }}
                        style={{
                            position: "fixed",
                            top: 20,
                            // Мобайл: справа от панели (100vw - 52px + 8px отступ)
                            // Десктоп: динамически от sidebarW
                            left: window.innerWidth < 900 ? "calc(100vw - 52px)" : sidebarW + 16,
                            zIndex: 300,
                        }}
                    >
                        <Box
                            onClick={() => setFolderSidebarOpen(false)}
                            sx={{
                                width: 36, height: 36,
                                borderRadius: "50%",
                                bgcolor: "neutral.100",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                cursor: "pointer",
                                color: "text.tertiary",
                                transition: "color 0.15s",
                                "&:hover": { color: "danger.plainColor" },
                                ml: 1,
                            }}
                        >
                            <CloseRoundedIcon sx={{ fontSize: 17 }} />
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>

            <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0, bgcolor: "background.body" }}>
                {needsLoad || listLoading ? <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ flex: 1, p: 3 }}>
                    {listError ? <><Typography color="danger">{listError}</Typography><Button size="sm" variant="soft" onClick={() => setListRevision(v => v + 1)}>Повторить загрузку списка</Button></> : <><CircularProgress size="sm" /><Typography level="body-sm">Загрузка выбранного списка…</Typography></>}
                </Stack> : <AnimatePresence mode="wait" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <TaskEditor key={task?.id || "empty"} task={task} onUpdate={handleUpdate} setFolderSidebarOpen={setFolderSidebarOpen} />
                </AnimatePresence>}
            </Box>
        </Box>
    );
}
